import { useMemo } from 'react';
import NotesList from './components/NotesList';
import { Outlet, useNavigate } from 'react-router-dom';
import Pagination, { usePagination } from '@/components/Common/Pagination';
import LoadingSkeleton from './components/LoadingSkeleton';
import { Box, Button, Stack, Typography } from '@mui/material';
import { EmptyStateView } from '@/components/Common/EmptyStateView';
import { useGetIntraofficeNotes } from '@/apis/agency/intraofficeNotes/hook';
import { useGetBusinesses } from 'apis/agency/hook';
import { useForm } from 'react-hook-form';
import { BusinessProfileResponse } from 'apis/business/types';
import { GoPlus } from 'react-icons/go';
import Select from '@/components/Common/Select';

export default function IntraofficeNotes() {
  const navigate = useNavigate();
  const { currentPage, perPage, setPage, setPerPage } = usePagination();

  const { control, watch } = useForm({
    defaultValues: {
      filter: { business_id_eq: ' ' }
    }
  });

  const filters = watch();

  const { data: businesses } = useGetBusinesses({
    per_page: 9999,
    filter: { active_and_past_due_and_inactive: true }
  });

  const { data: intraofficeNotesData, isLoading } = useGetIntraofficeNotes({
    page: currentPage,
    per_page: perPage,
    ...filters
  });

  const notes = useMemo(() => intraofficeNotesData?.data, [intraofficeNotesData?.data]);

  return (
    <>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', md: 'center' }}
        mb={3}
        gap={2}
      >
        <Box>
          <Typography fontWeight={500} fontSize='20px'>
            Team Notes
          </Typography>
          <Typography color='secondary' fontSize={14}>
            Track important business activities and team updates
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} width={{ xs: '100%', md: 'auto' }}>
          <Box minWidth={{ xs: '100%', sm: 250 }}>
            <Select
              name='filter.business_id_eq'
              label='Business'
              searchable
              control={control}
              size='small'
              options={[
                { value: ' ', label: 'All Business' },
                ...(businesses?.data.map((business: BusinessProfileResponse) => ({
                  value: business.id,
                  label: business.name
                })) ?? [])
              ]}
              sx={{ height: '40px', bgcolor: 'common.white' }}
            />
          </Box>

          <Button
            variant='contained'
            startIcon={<GoPlus />}
            sx={{ minHeight: '40px', width: '124px', borderRadius: '100px' }}
            onClick={() => navigate('add')}
          >
            Add Note
          </Button>
        </Stack>
      </Stack>

      {isLoading ? (
        <LoadingSkeleton />
      ) : notes?.length ? (
        <NotesList notes={notes} />
      ) : (
        <Box mt={10}>
          <EmptyStateView />
        </Box>
      )}

      <Pagination
        totalPages={intraofficeNotesData?.meta?.total_pages}
        totalCount={intraofficeNotesData?.meta?.total_count ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />

      <Outlet />
    </>
  );
}
